// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";

// IMPORT UI COMPONENTS
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  downloadHtmlAsPdf,
  printHtmlContent,
} from "../../../shared/iosPwaPrint";

import { useLocation } from "react-router-dom";
import { GetOpdDetailsForReport } from "../../../services/TokenService";
import { PreferencesContext } from "../../../PreferenceContext";
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { GetPrescriptionTokenDetail } from "../../../services/ReferBookEntryService";
import PrescriptionDetailsForReport from "./PrescriptionDetailsForReport";

interface BranchDetails {
  opdId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  departmentId: number;
  specialityId: number;
  patientId: number;
}

const PrescriptionReport = () => {
  const initialBranchDetails: BranchDetails = {
    opdId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    departmentId: 0,
    specialityId: 0,
    patientId: 0,
  };

  const edService = new EncryptDecryptService();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openPrescriptionReport, setOpenPrescriptionReport] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);

  const [branchDetails, setBranchDetails] =
    useState<BranchDetails>(initialBranchDetails);
  const [detailedOpd, setDetailedOpd] = React.useState(null);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const smallScreen = useMediaQuery("(max-width:768px)");

  const htmlprintRef = useRef<any>();
  const zoomOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = "";
  let opdId = urlParams.get("q");
  let curOpdId = 0;

  if (opdId !== null && opdId !== undefined) {
    let decId = edService.decrypt(opdId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curOpdId = +decId;
    }
  }
  if (token !== null && token !== undefined) {
    currentToken = token;
  }

  const getPrescriptionTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetPrescriptionTokenDetail(
        params,
        currentToken,
      );

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          opdId: data.opdId ?? 0,
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          departmentId: data.departmentId ?? 0,
          specialityId: data.specialityId ?? 0,
          patientId: data.patientId ?? 0,
          speciality: data.speciality ?? "",
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  const fetchDetailedOpdforReport = async (
    opdId: string,
    consumerId: string,
    organizationId: string,
    branchId: string,
    departmentId: string,
    specialityId: string,
    patientId: string,
  ) => {
    try {
      let params = new URLSearchParams();
      let reportData = null;
      params.append("opdId", opdId);
      params.append("consumerId", consumerId);
      params.append("branchId", branchId);
      params.append("organizationId", organizationId);
      params.append("departmentId", departmentId);
      params.append("specialityId", specialityId);
      params.append("patientId", patientId);
      const result: any = await GetOpdDetailsForReport(params, currentToken);
      if (result?.data?.data) {
        reportData = result?.data?.data;
        setDetailedOpd(reportData);
      }
      return reportData;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getPrescriptionTokenDetail();
      if (tokenData?.opdId || 0) {
        await fetchDetailedOpdforReport(
          tokenData.opdId || 0,
          tokenData.consumerId || 0,
          tokenData.organizationId || 0,
          tokenData.branchId || 0,
          tokenData.departmentId || 0,
          tokenData.specialityId || 0,
          tokenData.patientId || 0,
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const handleZoomChange = (event: any) => {
    setZoomLevel(parseFloat(event.target.value));
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      const nextZoom = zoomOptions.find((z) => z > prev) || prev;
      return nextZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const nextZoom = [...zoomOptions].reverse().find((z) => z < prev) || prev;
      return nextZoom;
    });
  };

  const handlePrintHtml = () => {
    if (htmlprintRef.current) {
      printHtmlContent(htmlprintRef.current, {
        printable: htmlprintRef.current.innerHTML,
        type: "raw-html",
        maxWidth: 1000,
        documentTitle: detailedOpd?.caseNumber?.toString() || "prescription",
        style: ` @media print {
        body {
          padding: 0;
          background-color: #fff;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          font-family: Arial, sans-serif !important;
        }
        @page {
          size: auto;
          margin:0.80in;
        }
        .print-conatiner{
          display: flex;
          flex-direction: column;
          min-height:100vh;
          position:relative;
        }
        .page-break {
          page-break-before: always;
          top: 100px;                      
        }
        .address-container {
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center;
          font-size: 12.5px;
        }
        .patientDetails {
          margin-top: 0 !important;
          padding-top: 0 !important;
          font-size: 12px !important;
        }
        .content{
          flex-grow:1;
        }
        .footer {
          bottom: 0;
          left: 0;
          width: 100%;
        }
        .footer::after{
          content:'';
          display: block;
          height: 50px;
        }
        * {
          font-family: Arial, sans-serif !important;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
      }
      `,
        scanStyles: true,
        targetStyles: ["*"],
      });
    }
  };

  const handlePDFDownload = async () => {
    const element = htmlprintRef.current as HTMLElement | null;
    if (!element) return;
    let documentName = detailedOpd?.caseNumber?.toString() || "prescription";
    documentName = documentName.replace(/\//g, "_");
    const patientName = detailedOpd?.patientName || "patient";

    await downloadHtmlAsPdf(element, {
      filename: `${documentName}_${patientName}.pdf`,
      margin: [10, 10, 10, 10],
    });
  };

  const handleCloseHTMLPopup = (event: any, reason: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setOpenPrescriptionReport(false);
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [opdId]);

  return (
    <Dialog
      fullScreen
      open={openPrescriptionReport}
      onClose={handleCloseHTMLPopup}
      scroll="paper"
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "4px",
          height: "100vh",
          margin: "0px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "4px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0}
            sx={{ padding: 0, margin: 0 }}
          >
            <Select
              value={zoomLevel}
              onChange={handleZoomChange}
              sx={{
                mx: 1,
                minWidth: 70,
                border: "none",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                backgroundColor: "transparent",
                padding: 0,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1px solid #c7c5c5",
                    borderRadius: "4px",
                  },
                },
              }}
            >
              {zoomOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography
                    sx={{
                      fontSize: "13px !important",
                    }}
                  >
                    {(option * 100).toFixed(0)}%
                  </Typography>
                </MenuItem>
              ))}
            </Select>

            <Box display="flex" gap={1}>
              <Tooltip title="Zoom Out">
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  sx={{ padding: 0 }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom In">
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 2}
                  sx={{ padding: 0 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Tooltip title="Print PDF" placement="top">
              <IconButton aria-label="Print PDF" onClick={handlePrintHtml}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF" placement="top">
              <IconButton aria-label="Download PDF" onClick={handlePDFDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          padding: "16px !important",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#f1f3f5",
          boxSizing: "border-box",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "max-content",
            border: "1px solid #ddd",
            boxShadow: "4px rgba(0, 0, 0, 0.2)",
            borderRadius: "2px",
            padding: "30px 50px",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "white",
            boxSizing: "border-box",
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center",
            transition: "transform 0.2s ease-in-out",
            margin: "0 auto",
          }}
        >
          <Box ref={htmlprintRef} sx={{ width: "794px" }}>
            <PrescriptionDetailsForReport
              opdData={detailedOpd}
              tokenCall={branchDetails}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionReport;
